import { computed } from 'vue'
import { PhoneCodes, type PhoneCode } from '@/constants/phone-codes'

/**
 * Composable для работы с локализованными названиями стран для телефонных кодов.
 * Использует локаль интерфейса (i18n), выбранную пользователем, а не язык браузера.
 */
export function usePhoneCodeLocalization() {
  const { locale: i18nLocale } = useI18n()

  /**
   * Получает локализованное название страны по ISO коду
   */
  const getLocalizedCountryName = (isoCode: string, locale: string): string => {
    try {
      const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
      return displayNames.of(isoCode) || isoCode
    } catch {
      // Fallback на английский, если локаль не поддерживается
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
        return displayNames.of(isoCode) || isoCode
      } catch {
        return isoCode
      }
    }
  }

  /**
   * Текущая локаль интерфейса (выбранная пользователем через роут/i18n), не язык браузера.
   */
  const currentLocale = computed(() => i18nLocale.value || 'uk')

  /**
   * Получает локализованное название страны для отображения.
   */
  const getDisplayName = (item: PhoneCode): string => {
    return getLocalizedCountryName(item.isoCode, currentLocale.value)
  }

  /**
   * Сортирует PhoneCodes по локализованным названиям
   */
  const sortedPhoneCodes = computed(() => {
    const locale = currentLocale.value
    const codes = [...PhoneCodes]

    return codes.sort((a, b) => {
      const nameA = getLocalizedCountryName(a.isoCode, locale).toLowerCase()
      const nameB = getLocalizedCountryName(b.isoCode, locale).toLowerCase()
      return nameA.localeCompare(nameB, locale)
    })
  })

  return {
    getLocalizedCountryName,
    getDisplayName,
    sortedPhoneCodes,
    currentLocale,
  }
}

